import { PDFArray, PDFDict, PDFDocument, PDFName, PDFRef, PDFStream, rgb, pushGraphicsState, popGraphicsState, concatTransformationMatrix, drawObject, type PDFObject } from "pdf-lib";
import { encodeG4 } from "./g4";
import { packBitmap, type Jbig2Encoder } from "./jbig2/jbig2Core";
import { pageFootprints } from "./pageFootprints";
import { PAGE_OVERHEAD, MIN_PAGE_GAIN, addJbig2Image } from "./bilevelPdf";
import { CompressStopped } from "./imageRecompress";
import { analysePage, GRID_STAGES, nonBlankCells, RENDER_DPI, stageSetting, whitenPaper, type PageGrid } from "./tileGrid";

/**
 * The "pages as pictures" part of Compress PDF, with nothing in it that needs a browser: the site (targetCompression.ts)
 * and the API (utility-api core/pdf/ladder.ts) both run this file, each handing in how to open a PDF, make a canvas and
 * write a JPEG (PageEnv). So the levels, the page grid, the piece assembly and the stop-at-goal rule are one piece of code.
 */

/** Just the canvas surface this code touches. A browser canvas, node-canvas and @napi-rs/canvas all fit it. */
export interface Ctx2D {
  fillStyle: any;
  imageSmoothingQuality: any;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(image: any, ...args: number[]): void;
  getImageData(x: number, y: number, w: number, h: number): { data: Uint8ClampedArray };
  putImageData(data: any, x: number, y: number): void;
}
export interface CanvasLike { width: number; height: number; getContext(type: "2d"): Ctx2D | null }

/** A PDF opened for reading and rendering. */
export interface OpenPdf {
  numPages: number;
  /** A pdf.js page (only getViewport, getAnnotations and getOperatorList are used). */
  getPage(pageNumber: number): Promise<PdfjsPageLike>;
  /** Renders a page onto a white canvas at `scale` (1 = 72 dpi), the way it prints. */
  renderCanvas(pageNumber: number, scale: number): Promise<CanvasLike>;
}
export interface PdfjsPageLike {
  getViewport(params: { scale: number }): { width: number; height: number };
  getAnnotations(): Promise<unknown[]>;
  getOperatorList(): Promise<{ fnArray: ArrayLike<number> }>;
}

/** What the environment (browser or Node) provides. */
export interface PageEnv {
  open(): Promise<OpenPdf>;
  makeCanvas(width: number, height: number): CanvasLike;
  /** The canvas as a JPEG; quality 0 to 1. */
  jpeg(canvas: CanvasLike, quality: number): Promise<Uint8Array>;
  loadJbig2(): Promise<Jbig2Encoder>;
}

/** A page's own size in points (what a reader shows and a printer prints), independent of the render scale. */
export interface PageBox {
  width: number;
  height: number;
}

type Rect = { x0: number; y0: number; x1: number; y1: number };
/** One rectangle of the page, scaled by `ratio` and written as a JPEG. */
async function encodeRect(env: PageEnv, src: CanvasLike, r: Rect, ratio: number, quality: number): Promise<Uint8Array> {
  const w = r.x1 - r.x0, h = r.y1 - r.y0;
  const c = env.makeCanvas(Math.max(1, Math.round(w * ratio)), Math.max(1, Math.round(h * ratio)));
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported in this browser");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(src, r.x0, r.y0, w, h, 0, 0, c.width, c.height);
  const bytes = await env.jpeg(c, quality);
  c.width = c.height = 0;
  return bytes;
}

const RECT_OVERHEAD = 120;
const FILL_OVERHEAD = 30;

/** One finished level: a complete, downloadable file. */
export interface LevelCheckpoint {
  level: number;
  name: string;
  /** One plain sentence on what this level did and what it costs. */
  detail: string;
  bytes: Uint8Array;
  size: number;
  /** Text on the converted pages is no longer selectable. */
  turnsPagesToPictures: boolean;
  pagesConverted?: number;
  pagesKept?: number;
}

export interface PageLevelOptions {
  /** The best file so far that still has real text; pages that are not worth converting are copied from it. */
  base: Uint8Array;
  /** Stop as soon as a level is at or under this many bytes. */
  goal: number;
  /** A level is only worth showing when it is smaller than this (the original file, or the best result so far). */
  mustBeat: number;
  /** Number given to the first level this function finishes. */
  firstLevel: number;
  stamp?: (doc: PDFDocument) => void;
  onLevel?: (cp: LevelCheckpoint) => void;
  onProgress?: (msg: string) => void;
  shouldStop?: () => boolean;
  /** Also run the extreme steps (greyscale at low resolution, then pure black and white) after the picture levels. */
  extreme?: boolean;
}

/** One stored piece of a page: a picture, or (no `jpg`) a plain fill. Coordinates are in pixels of the 200 dpi render. */
interface Piece { r: Rect; jpg?: Uint8Array; fill?: [number, number, number]; g4?: { data: Uint8Array; w: number; h: number }; jb2?: { data: Uint8Array; w: number; h: number; globals?: Uint8Array } }

interface PageData {
  w: number; h: number;
  /** False for a page that was never looked at because pages like it cannot get smaller as pictures. */
  seen: boolean;
  grid: PageGrid | null;
  /** Pieces for the level being built; cleared once that level is assembled. */
  pieces: Piece[] | null;
  /** Encoded grid rectangles, keyed by rectangle index and setting. */
  enc: Map<string, Uint8Array>;
}

const encKey = (i: number, ratio: number, quality: number) => `${i}:${ratio}:${quality}`;

function pieceBytes(d: PageData): number {
  if (!d.seen || !d.pieces) return Infinity;
  let n = PAGE_OVERHEAD;
  for (const p of d.pieces) n += p.g4 ? p.g4.data.length + 200 : p.jb2 ? p.jb2.data.length + 200 : p.jpg ? p.jpg.length + RECT_OVERHEAD : FILL_OVERHEAD;
  return n;
}

export const QUADRANT_LEVEL = { name: "Pages as pictures, empty quarters dropped", detail: "Each remaining page is turned into a 200 dpi picture and cut into 4 quarters. Quarters with nothing in them are not stored at all. Text can no longer be selected on these pages." };
export const SIXTEENTH_LEVEL = { name: "Pages split finer, empty areas dropped", detail: "The quarters that still hold something are cut again into 4, and the empty pieces are dropped." };
export const BW_LEVELS = [
  { dpi: 150, name: "Black-and-white pages at 150 dpi", detail: "Pages are stored as pure black and white (a format about ten times smaller than a picture for text). Shades of grey and colour are lost, so check that the pages still look right." },
  { dpi: 120, name: "Black-and-white pages at 120 dpi", detail: "The same black-and-white pages at 120 dpi. Small print gets rough; stop at the level before if it is hard to read." },
];
export const CLEAN_LEVEL = { name: "Paper speckle cleaned", detail: "Scanner dust, shadows and faint paper tint are set to plain white so empty areas can be dropped completely." };
export const GREY_LEVEL = { name: "Greyscale pages at 70 dpi", detail: "Every page becomes a grey picture at 70 dpi: colour is lost and small print gets soft, but shading stays. Text can no longer be selected." };
export const JBIG2_LOSSLESS_LEVEL = { name: "Black and white, lossless (JBIG2)", detail: "Pages are stored as pure black and white at 150 dpi, exactly as read, with no guessing. Colour, shading and photos are lost, so check that the pages still look right." };
export const JBIG2_SMALLEST_LEVEL = { name: "Black and white, smallest (JBIG2)", detail: "Letters that look alike are stored once and reused, which makes the file much smaller. Very rarely a letter can be swapped for a look-alike one, so read important numbers in the original. Colour, shading and photos are lost." };

/**
 * Turns the remaining pages into pictures one level at a time, gentlest first, handing back a complete file after
 * each level so the person can see the size at every step and stop whenever they like.
 *
 *   200 dpi quarters (empty dropped) -> sixteenths (empty dropped) -> [paper cleaned] -> 140 dpi areas ->
 *   flat areas as fills -> photos 70 dpi -> text+picture 100 dpi -> text 100 dpi (small print/math 120) -> text 85 dpi
 *
 * Each page is judged on its own: it becomes a picture only when that is clearly cheaper than the page as it is
 * (so a text page that is already small stays real text).
 */
export async function compressPagesInLevelsWith(env: PageEnv, opts: PageLevelOptions): Promise<{ bytes: Uint8Array; size: number; pagesConverted: number; pagesKept: number } | null> {
  const stop = () => { if (opts.shouldStop?.()) throw new CompressStopped(); };
  const pdfjsDoc = await env.open();
  const numPages = pdfjsDoc.numPages;
  const src = await PDFDocument.load(opts.base, { ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false });
  const footprints = pageFootprints(src);
  const boxes: PageBox[] = [];
  for (let i = 1; i <= numPages; i++) {
    const vp1 = (await pdfjsDoc.getPage(i)).getViewport({ scale: 1 });
    boxes.push({ width: vp1.width, height: vp1.height });
  }
  const scale = RENDER_DPI / 72;
  const data: PageData[] = Array.from({ length: numPages }, () => ({ w: 1, h: 1, seen: false, grid: null, pieces: null, enc: new Map<string, Uint8Array>() }));

  let level = opts.firstLevel;
  let best: { bytes: Uint8Array; size: number; pagesConverted: number; pagesKept: number } | null = null;
  const sizeToBeat = () => (best ? best.size : Math.min(opts.base.length, opts.mustBeat));
  const reached = () => !!best && best.size <= opts.goal;
  const worthIt = (k: number) => pieceBytes(data[k]) < footprints[k] * (1 - MIN_PAGE_GAIN);

  // Builds the file for the pieces now stored. Returns null when no page is worth converting.
  const build = async (): Promise<{ bytes: Uint8Array; converted: number } | null> => {
    const useRaster = data.map((_, k) => worthIt(k));
    const converted = useRaster.filter(Boolean).length;
    if (!converted) return null;
    const keepIdx: number[] = [];
    useRaster.forEach((u, k) => { if (!u) keepIdx.push(k); });
    const out = await PDFDocument.create();
    const copied = keepIdx.length ? await out.copyPages(src, keepIdx) : [];
    const globalsRefs = new Map<Uint8Array, ReturnType<typeof out.context.register>>();
    let kept = 0;
    for (let k = 0; k < numPages; k++) {
      if (!useRaster[k]) { out.addPage(copied[kept++]); continue; }
      const d = data[k], box = boxes[k];
      const page = out.addPage([box.width, box.height]);
      const sx = box.width / d.w, sy = box.height / d.h;
      const place = (r: Rect) => ({ x: r.x0 * sx, y: box.height - r.y1 * sy - 0.3, width: (r.x1 - r.x0) * sx + 0.3, height: (r.y1 - r.y0) * sy + 0.3 });
      const drawBw = (ref: ReturnType<typeof out.context.register>, r: Rect) => {
        const name = page.node.newXObject("Bw", ref);
        const q = place(r);
        page.pushOperators(pushGraphicsState(), concatTransformationMatrix(q.width, 0, 0, q.height, q.x, q.y), drawObject(name), popGraphicsState());
      };
      for (const p of d.pieces!) if (p.fill) page.drawRectangle({ ...place(p.r), color: rgb(p.fill[0] / 255, p.fill[1] / 255, p.fill[2] / 255), borderWidth: 0 });
      for (const p of d.pieces!) if (p.jpg) page.drawImage(await out.embedJpg(p.jpg), place(p.r));
      for (const p of d.pieces!) if (p.g4) {
        drawBw(out.context.register(out.context.stream(p.g4.data, {
          Type: "XObject", Subtype: "Image", Width: p.g4.w, Height: p.g4.h, ColorSpace: "DeviceGray", BitsPerComponent: 1,
          Filter: "CCITTFaxDecode", DecodeParms: { K: -1, Columns: p.g4.w, Rows: p.g4.h },
        })), p.r);
      }
      for (const p of d.pieces!) if (p.jb2) drawBw(addJbig2Image(out, p.jb2, globalsRefs), p.r);
    }
    opts.stamp?.(out);
    return { bytes: new Uint8Array(await out.save({ useObjectStreams: true })), converted };
  };

  // Builds the level and reports it when it is smaller than the best so far.
  const assemble = async (stage: { name: string; detail: string }): Promise<boolean> => {
    stop();
    opts.onProgress?.(`Building level ${level}: ${stage.name}...`);
    const built = await build();
    if (!built) return false;
    const { bytes, converted } = built;
    if (bytes.length >= sizeToBeat() * 0.995) return false;
    best = { bytes, size: bytes.length, pagesConverted: converted, pagesKept: numPages - converted };
    opts.onLevel?.({ level, name: stage.name, detail: stage.detail, bytes, size: bytes.length, turnsPagesToPictures: true, pagesConverted: converted, pagesKept: numPages - converted });
    level++;
    return true;
  };

  const pixelsOf = (c: CanvasLike) => c.getContext("2d")!.getImageData(0, 0, c.width, c.height).data;
  const release = (c: CanvasLike) => { c.width = c.height = 0; };
  const cellPieces = async (canvas: CanvasLike, cells: Rect[]): Promise<Piece[]> => {
    const out: Piece[] = [];
    for (const r of cells) out.push({ r, jpg: await encodeRect(env, canvas, r, 1, 0.85) });
    return out;
  };

  const order = footprints.map((_, k) => k).sort((a, b) => footprints[b] - footprints[a]);
  const pictureLevels = async () => {
    // Pass 1: 200 dpi pages cut into quarters, heaviest pages first. A page can only get smaller as pictures if it
    // is heavy now, so once a run of pages in a row clearly cannot (a scan that is already tight) the lighter pages
    // after them cannot either and are left alone.
    const HOPELESS_RUN = 12;
    let hopeless = 0, looked = 0;
    for (const k of order) {
      stop();
      if (hopeless >= HOPELESS_RUN) break;
      if (footprints[k] < 1500) continue; // an empty or nearly empty page cannot get smaller
      looked++;
      opts.onProgress?.(`Turning pages into pictures: ${looked} done (heaviest pages first)...`);
      const canvas = await pdfjsDoc.renderCanvas(k + 1, scale);
      const px = pixelsOf(canvas);
      const cells = nonBlankCells(px, canvas.width, canvas.height, 2, 2);
      const pieces = await cellPieces(canvas, cells);
      data[k] = { w: canvas.width, h: canvas.height, seen: true, grid: null, pieces, enc: new Map<string, Uint8Array>() };
      release(canvas);
      // Best case, every later level together shrinks the sharpest pictures to about a quarter.
      hopeless = pieceBytes(data[k]) * 0.25 > footprints[k] * (1 - MIN_PAGE_GAIN) ? hopeless + 1 : 0;
    }
    const live = () => data.map((d, k) => (d.seen && (pieceBytes(d) * 0.25 < footprints[k] * (1 - MIN_PAGE_GAIN)) ? k : -1)).filter((k) => k >= 0);
    await assemble(QUADRANT_LEVEL);
    if (reached()) return;
    const worth = live();
    if (!worth.length) return;

    // Pass 2: the same pages cut into sixteenths.
    const redo = async (label: string, whiten: boolean, each: (k: number, canvas: CanvasLike, px: Uint8ClampedArray) => Promise<void>) => {
      for (const k of worth) {
        stop();
        opts.onProgress?.(`${label}: page ${k + 1} of ${numPages}...`);
        const canvas = await pdfjsDoc.renderCanvas(k + 1, scale);
        const px = pixelsOf(canvas);
        if (whiten) whitenPaper(px);
        await each(k, canvas, px);
        release(canvas);
      }
    };
    let nonBlank = 0, cellCount = 0;
    await redo("Splitting pages finer", false, async (k, canvas, px) => {
      const cells = nonBlankCells(px, canvas.width, canvas.height, 4, 4);
      nonBlank += cells.length; cellCount += 16;
      data[k].pieces = await cellPieces(canvas, cells);
    });
    await assemble(SIXTEENTH_LEVEL);
    if (reached()) return;

    // Optional: pages where almost nothing counts as empty are usually scans with speckle or a tinted paper.
    let whiten = false;
    if (cellCount && nonBlank / cellCount >= 0.8) {
      const before = worth.reduce((n, k) => n + pieceBytes(data[k]), 0);
      await redo("Cleaning scanned paper", true, async (k, canvas, px) => {
        const cells = nonBlankCells(px, canvas.width, canvas.height, 4, 4);
        data[k].pieces = await cellPieces(canvas, cells);
      });
      const after = worth.reduce((n, k) => n + pieceBytes(data[k]), 0);
      if (after < before * 0.95) { whiten = true; await assemble(CLEAN_LEVEL); if (reached()) return; }
    }

    // Pass 3: the 10 x 20 grid of each page, then every level that lowers the sharpness of one kind of area.
    await redo("Studying page areas", whiten, async (k, canvas, px) => {
      const grid = analysePage(px, canvas.width, canvas.height);
      const d = data[k];
      d.grid = grid;
      for (const g of GRID_STAGES) {
        for (let i = 0; i < grid.rects.length; i++) {
          const st = stageSetting(g.stage, grid.rects[i].kind);
          if (!st || st === "fill") continue;
          const key = encKey(i, st.ratio, st.quality);
          if (!d.enc.has(key)) d.enc.set(key, await encodeRect(env, canvas, grid.rects[i], st.ratio, st.quality));
        }
      }
    });
    for (const g of GRID_STAGES) {
      for (const k of worth) {
        const d = data[k];
        const pieces: Piece[] = [];
        d.grid!.rects.forEach((r, i) => {
          const st = stageSetting(g.stage, r.kind);
          if (!st) return;
          if (st === "fill") pieces.push({ r, fill: r.color ?? [255, 255, 255] });
          else pieces.push({ r, jpg: d.enc.get(encKey(i, st.ratio, st.quality))! });
        });
        d.pieces = pieces;
      }
      await assemble(g);
      if (reached()) return;
    }
  };

  // Last resort, when the picture levels cannot go further: pure black-and-white pages in CCITT Group 4, which a
  // browser cannot write natively but PDF readers all decode. Only pages that get clearly smaller are converted.
  const bilevelLevels = async () => {
    const BW_RUN = 12;
    let run = 0, n = 0;
    const got = new Map<number, { dpi: number; g4: { data: Uint8Array; w: number; h: number } }[]>();
    for (const k of order) {
      stop();
      if (run >= BW_RUN) break;
      if (footprints[k] < 3000) continue;
      n++;
      opts.onProgress?.(`Trying black-and-white pages: ${n} done (heaviest pages first)...`);
      const canvas = await pdfjsDoc.renderCanvas(k + 1, BW_LEVELS[0].dpi / 72);
      const list: { dpi: number; g4: { data: Uint8Array; w: number; h: number } }[] = [];
      for (const lv of BW_LEVELS) {
        const ratio = lv.dpi / BW_LEVELS[0].dpi;
        const c = env.makeCanvas(Math.max(1, Math.round(canvas.width * ratio)), Math.max(1, Math.round(canvas.height * ratio)));
        const cx = c.getContext("2d")!;
        cx.imageSmoothingQuality = "high";
        cx.fillStyle = "#fff"; cx.fillRect(0, 0, c.width, c.height);
        cx.drawImage(canvas, 0, 0, c.width, c.height);
        const rgba = cx.getImageData(0, 0, c.width, c.height).data;
        const bits = new Uint8Array(c.width * c.height);
        for (let p = 0; p < bits.length; p++) bits[p] = rgba[p * 4 + 1] < 150 ? 1 : 0;
        list.push({ dpi: lv.dpi, g4: { data: encodeG4(bits, c.width, c.height), w: c.width, h: c.height } });
        release(c);
      }
      release(canvas);
      got.set(k, list);
      const smallest = list[list.length - 1].g4.data.length + PAGE_OVERHEAD + 200;
      run = smallest >= footprints[k] * (1 - MIN_PAGE_GAIN) ? run + 1 : 0;
    }
    for (let li = 0; li < BW_LEVELS.length; li++) {
      for (let k = 0; k < numPages; k++) {
        const d = data[k], g = got.get(k)?.[li];
        if (!g) { d.seen = false; d.pieces = null; continue; }
        d.seen = true; d.w = g.g4.w; d.h = g.g4.h;
        d.pieces = [{ r: { x0: 0, y0: 0, x1: g.g4.w, y1: g.g4.h }, g4: g.g4 }];
      }
      await assemble(BW_LEVELS[li]);
      if (reached()) return;
    }
  };

  // Extreme steps, after every picture level: greyscale pages at 70 dpi, then pure black-and-white JBIG2 (exact, then
  // with shared letter shapes). Every file that beats the original is reported, smallest last, so the person can
  // pick any of them. Returns false when the JBIG2 encoder cannot load or nothing gets smaller, so the plain CCITT
  // levels can run instead.
  const JB_DPI = 150, GREY_DPI = 70;
  const emitFile = (name: string, detail: string, bytes: Uint8Array, converted: number) => {
    if (bytes.length >= opts.mustBeat * 0.995) return false;
    if (!best || bytes.length < best.size) best = { bytes, size: bytes.length, pagesConverted: converted, pagesKept: numPages - converted };
    opts.onLevel?.({ level, name, detail, bytes, size: bytes.length, turnsPagesToPictures: true, pagesConverted: converted, pagesKept: numPages - converted });
    level++;
    return true;
  };
  const extremeLevels = async (): Promise<boolean> => {
    let enc;
    try { enc = await env.loadJbig2(); } catch { return false; }
    const RUN = 12;
    let run = 0, n = 0;
    const bits = new Map<number, { bits: Uint8Array; w: number; h: number }>();
    const lossless = new Map<number, Uint8Array>();
    const grey = new Map<number, { jpg: Uint8Array; w: number; h: number }>();
    for (const k of order) {
      stop();
      if (run >= RUN) break;
      if (footprints[k] < 3000) continue;
      n++;
      opts.onProgress?.(`Extreme steps: ${n} pages done (heaviest pages first)...`);
      const canvas = await pdfjsDoc.renderCanvas(k + 1, JB_DPI / 72);
      const w = canvas.width, h = canvas.height;
      const packed = packBitmap(pixelsOf(canvas), w, h, 150);
      const data1 = enc.generic(packed, w, h, JB_DPI);
      bits.set(k, { bits: packed, w, h });
      lossless.set(k, data1);
      // Greyscale keeps the shading but drops colour; pictures and text are both stored at 70 dpi.
      const cx = canvas.getContext("2d")!;
      const img = cx.getImageData(0, 0, w, h);
      const px = img.data;
      for (let p = 0; p < px.length; p += 4) { const g = (px[p] * 77 + px[p + 1] * 151 + px[p + 2] * 28) >> 8; px[p] = px[p + 1] = px[p + 2] = g; }
      cx.putImageData(img, 0, 0);
      grey.set(k, { jpg: await encodeRect(env, canvas, { x0: 0, y0: 0, x1: w, y1: h }, GREY_DPI / JB_DPI, 0.5), w: Math.round(w * GREY_DPI / JB_DPI), h: Math.round(h * GREY_DPI / JB_DPI) });
      release(canvas);
      run = data1.length + PAGE_OVERHEAD + 200 >= footprints[k] * (1 - MIN_PAGE_GAIN) ? run + 1 : 0;
    }
    if (!bits.size) return false;
    const setPieces = (make: (k: number) => Piece | null) => {
      for (let k = 0; k < numPages; k++) {
        const b = bits.get(k), pc = b ? make(k) : null;
        const d = data[k];
        if (!b || !pc) { d.seen = false; d.pieces = null; continue; }
        d.seen = true; d.w = pc.r.x1; d.h = pc.r.y1; d.pieces = [pc];
      }
    };
    const found: { name: string; detail: string; bytes: Uint8Array; converted: number }[] = [];
    const finish = async (name: string, detail: string) => {
      stop();
      const built = await build();
      if (built) found.push({ name, detail, bytes: built.bytes, converted: built.converted });
    };
    opts.onProgress?.("Building the greyscale file...");
    setPieces((k) => { const g = grey.get(k)!; return { r: { x0: 0, y0: 0, x1: g.w, y1: g.h }, jpg: g.jpg }; });
    await finish(GREY_LEVEL.name, GREY_LEVEL.detail);
    opts.onProgress?.("Building the lossless black-and-white file...");
    setPieces((k) => { const b = bits.get(k)!; return { r: { x0: 0, y0: 0, x1: b.w, y1: b.h }, jb2: { data: lossless.get(k)!, w: b.w, h: b.h } }; });
    await finish(JBIG2_LOSSLESS_LEVEL.name, JBIG2_LOSSLESS_LEVEL.detail);
    // Symbol mode: the pages that are worth converting share one table of letter shapes.
    opts.onProgress?.("Building the smallest black-and-white file (shared letter shapes)...");
    try {
      const keep = Array.from(bits.keys()).filter((k) => { const d = data[k]; return d.seen && worthIt(k); }).sort((a, b) => a - b);
      if (keep.length) {
        enc.symbolBegin(0.85, JB_DPI);
        for (const k of keep) { const b = bits.get(k)!; stop(); enc.symbolAdd(b.bits, b.w, b.h); }
        const { globals, page } = enc.symbolFinish();
        const pages = keep.map((_, i) => page(i));
        enc.symbolEnd();
        const idx = new Map(keep.map((k, i) => [k, i] as const));
        setPieces((k) => { const i = idx.get(k); if (i === undefined) return null; const b = bits.get(k)!; return { r: { x0: 0, y0: 0, x1: b.w, y1: b.h }, jb2: { data: pages[i], w: b.w, h: b.h, globals } }; });
        await finish(JBIG2_SMALLEST_LEVEL.name, JBIG2_SMALLEST_LEVEL.detail);
      }
    } catch (e) { if (e instanceof CompressStopped) throw e; /* symbol mode is optional */ }
    found.sort((a, b) => b.bytes.length - a.bytes.length);
    let any = false;
    for (const f of found) if (emitFile(f.name, f.detail, f.bytes, f.converted)) any = true;
    return any;
  };

  await pictureLevels();
  if (opts.extreme && !reached() && !(await extremeLevels())) await bilevelLevels();
  return best;
}

/** Removes every object that nothing in the document points to any more (pdf-lib keeps them when it saves). */
function dropUnreachable(doc: PDFDocument): void {
  const ctx = doc.context;
  const seen = new Set<string>();
  const stack: PDFObject[] = [];
  const root = ctx.trailerInfo.Root, info = ctx.trailerInfo.Info;
  if (root) stack.push(root);
  if (info) stack.push(info);
  while (stack.length) {
    const o = stack.pop()!;
    let target: PDFObject | undefined = o;
    if (o instanceof PDFRef) {
      const key = o.toString();
      if (seen.has(key)) continue;
      seen.add(key);
      target = ctx.lookup(o);
    }
    if (!target) continue;
    if (target instanceof PDFDict) for (const v of target.values()) stack.push(v);
    else if (target instanceof PDFStream) for (const v of target.dict.values()) stack.push(v);
    else if (target instanceof PDFArray) for (let i = 0; i < target.size(); i++) stack.push(target.get(i));
  }
  for (const [ref] of ctx.enumerateIndirectObjects()) if (!seen.has(ref.toString())) ctx.delete(ref);
}

/** The file with the given (blank) pages emptied: same pages, sizes, bookmarks and links, nothing stored on them. */
export async function emptyPages(bytes: Uint8Array, pageIndexes: number[], stamp?: (doc: PDFDocument) => void): Promise<Uint8Array> {
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false });
  const pages = doc.getPages();
  for (const i of pageIndexes) {
    const node = pages[i].node;
    node.set(PDFName.of("Contents"), doc.context.register(doc.context.stream("")));
    node.set(PDFName.of("Resources"), doc.context.obj({}));
  }
  dropUnreachable(doc);
  stamp?.(doc);
  return new Uint8Array(await doc.save({ useObjectStreams: true }));
}
